import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Circle,
  Download,
  Loader2,
  RefreshCw,
  Sparkles,
  X,
} from 'lucide-react';
import { captureSupportScope, isSupportActivity } from '../admin/supportTransport';
import {
  companyContextApi,
  emptyCompanyProfile,
  type CompanyBusinessProfile,
  type ProfileField,
} from '../company-context/api';
import { BusinessBrief, ProfileEditor } from '../company-context/ProfileEditor';
import QuestionCard, { type QuestionAnswer } from '../company-context/QuestionCard';
import { useCompanyWorkspace } from '../components/companyWorkspaceContext';
import {
  AI_PLANS_CHANGED,
  isPlanActive,
  planApi,
  planReportUrl,
  planRunUrl,
  PlanApiError,
  type PlanClarification,
  type PlanPreparation,
  type PlanQuestion,
  type PlanRun,
  type PlanSettings,
} from './api';
import '../company-context/CompanyContext.css';
import './Plans.css';

const stages = [
  ['organisation', 'People & structure'],
  ['foundation', 'Vision, mission, values & objectives'],
  ['external', 'Market & competitor research'],
  ['internal', 'SWOT & success factors'],
  ['strategy', 'Strategy formulation'],
  ['execution', 'Projects & tasks'],
  ['report', 'Report & presentation'],
  ['exports', 'PDF & PowerPoint downloads'],
] as const;
function stageResultCounts(value: unknown) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const result = value as Record<string, unknown>;
  const refs =
    result.references && typeof result.references === 'object' && !Array.isArray(result.references)
      ? (result.references as Record<string, unknown>)
      : {};
  const count = (value: unknown) =>
    typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : undefined;
  return {
    generated: count(result.generated),
    preserved: count(result.preserved),
    rated: count(refs.ratedCriteria),
    unassessed: count(refs.unassessedCriteria),
  };
}
const statusLabel = (status: string, generationEnabled = true) =>
  ({
    queued: 'Queued',
    running: 'In progress',
    completed: 'Ready to review',
    completed_with_warnings: 'Ready · review notes',
    failed: generationEnabled ? 'Stopped · retry available' : 'Stopped · retry paused',
    needs_attention: 'Review needed',
    cancelled: 'Cancelled',
    revising: 'Updating your plan',
  })[status] || status.replace(/_/g, ' ');
const dateLabel = (value: string) =>
  Number.isNaN(Date.parse(value))
    ? value
    : new Date(value).toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: 'Australia/Sydney',
      });
const today = () =>
  new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'Australia/Sydney',
  }).format(new Date());
const afterYears = (date: string, years: number) => {
  const next = new Date(`${date}T00:00:00Z`);
  next.setUTCFullYear(next.getUTCFullYear() + years);
  return Number.isNaN(next.getTime()) ? '' : next.toISOString().slice(0, 10);
};
function clarificationBriefField(
  question: Pick<PlanQuestion, 'field' | 'scope'>
): ProfileField | null {
  if (question.scope !== 'business_context') return null;
  const field = question.field.trim().replace(/^(?:companyContext\.profile\.|profile\.)/, '');
  return Object.prototype.hasOwnProperty.call(emptyCompanyProfile, field)
    ? (field as ProfileField)
    : null;
}
export function applyProfileAnswer(
  profile: CompanyBusinessProfile,
  question: Pick<PlanQuestion, 'field' | 'scope'>,
  answer: string
): CompanyBusinessProfile {
  if (
    question.scope !== 'business_context' ||
    !(question.field in emptyCompanyProfile) ||
    !answer.trim()
  )
    return profile;
  const field = question.field as ProfileField;
  return {
    ...profile,
    [field]: Array.isArray(profile[field])
      ? answer
          .split(/\n/)
          .map((value) => value.trim())
          .filter(Boolean)
      : answer.trim(),
  };
}
const answerText = (question: PlanQuestion, value: QuestionAnswer) =>
  value.choiceId
    ? question.choices?.find((choice) => choice.id === value.choiceId)?.label || ''
    : value.answer.trim();
const draftKey = (companyId: string, suffix: string) =>
  `stratplan:ai-plan:${(window as any).Clerk?.user?.id || 'session'}:${captureSupportScope()}:${companyId}:${suffix}`;
function readDraft<T>(key: string): T | null {
  if (isSupportActivity()) return null;
  try {
    return JSON.parse(localStorage.getItem(key) || 'null');
  } catch {
    return null;
  }
}
function saveDraft(key: string, value: unknown) {
  if (isSupportActivity()) return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* The current answer remains in memory. */
  }
}
function clearDraft(key: string) {
  if (isSupportActivity()) return;
  try {
    localStorage.removeItem(key);
  } catch {
    /* Optional device draft. */
  }
}

export default function AIPlanPage() {
  const { companyId = '' } = useParams();
  const workspace = useCompanyWorkspace();
  const company = workspace?.company;
  const canWrite =
    workspace === null ||
    !!(company?.capabilities
      ? company.capabilities.editPlan && company.capabilities.editOrganisation
      : company?.role === 'owner' || company?.role === 'admin');
  return <PlanRunsPanel key={companyId} companyId={companyId} canWrite={canWrite} />;
}

export function PlanRunsPanel({
  companyId,
  practiceId,
  canWrite = true,
}: {
  companyId?: string;
  practiceId?: string;
  canWrite?: boolean;
}) {
  const [params, setParams] = useSearchParams();
  const selectedId = companyId ? params.get('run') : null;
  const requestedQuestionId = companyId ? params.get('question') || undefined : undefined;
  const [listing, setListing] = useState<{ enabled: boolean; runs: PlanRun[] } | null>(null);
  const [selected, setSelected] = useState<PlanRun | null>(null);
  const [preparation, setPreparation] = useState<PlanPreparation | null>(null);
  const [starting, setStarting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pollError, setPollError] = useState('');
  const generation = useRef(0);
  const polling = useRef(false);
  const current = useRef({ companyId, practiceId, selectedId });
  current.current = { companyId, practiceId, selectedId };
  const refresh = useCallback(async () => {
    const expected = generation.current;
    const scope = current.current;
    let list;
    let detail;
    try {
      list = await planApi.list({ companyId: scope.companyId, practiceId: scope.practiceId });
      if (expected !== generation.current) return;
      detail = scope.selectedId ? await planApi.get(scope.selectedId) : null;
    } catch (failure) {
      if (
        expected === generation.current &&
        failure instanceof PlanApiError &&
        [401, 403].includes(failure.status)
      ) {
        setListing(null);
        setSelected(null);
        setPreparation(null);
        setPollError('');
        setError(failure.message);
      }
      throw failure;
    }
    if (expected !== generation.current) return;
    if (detail && scope.companyId && detail.companyId !== scope.companyId)
      throw new Error('This run belongs to another company.');
    setListing(list);
    setSelected(detail);
    setPollError('');
  }, []);
  useEffect(() => {
    const expected = ++generation.current;
    setLoading(true);
    setError('');
    setSelected(null);
    setStarting(false);
    setPreparation(null);
    void refresh()
      .catch((failure) => {
        if (expected === generation.current) setError(failure.message);
      })
      .finally(() => {
        if (expected === generation.current) setLoading(false);
      });
    return () => {
      generation.current++;
    };
  }, [companyId, practiceId, selectedId, refresh]);
  useEffect(() => {
    if (!listing?.enabled) return;
    const poll = async () => {
      if (document.hidden || polling.current) return;
      polling.current = true;
      const expected = generation.current;
      try {
        await refresh();
      } catch (failure) {
        if (
          expected === generation.current &&
          !(failure instanceof PlanApiError && [401, 403].includes(failure.status))
        )
          setPollError(failure instanceof Error ? failure.message : 'Reconnecting to your plan…');
      } finally {
        polling.current = false;
      }
    };
    const timer = setInterval(() => void poll(), 6000);
    window.addEventListener(AI_PLANS_CHANGED, poll);
    return () => {
      clearInterval(timer);
      window.removeEventListener(AI_PLANS_CHANGED, poll);
    };
  }, [listing?.enabled, refresh]);
  useEffect(() => {
    if (selected?.id && !requestedQuestionId) window.scrollTo({ top: 0 });
  }, [selected?.id, requestedQuestionId]);
  const selectRun = (run: PlanRun) => {
    const next = new URLSearchParams(params);
    next.set('run', run.id);
    setParams(next);
  };
  const prepare = async () => {
    if (!companyId || !canWrite) return;
    const expected = generation.current;
    setStarting(true);
    setError('');
    try {
      const value = await planApi.preparation(companyId);
      if (expected === generation.current) setPreparation(value);
    } catch (failure) {
      if (expected === generation.current)
        setError(failure instanceof Error ? failure.message : 'Unable to prepare the plan.');
    } finally {
      if (expected === generation.current) setStarting(false);
    }
  };
  return (
    <div className="ai-plan-workspace">
      <header className="ai-plan-heading">
        <div>
          <p className="context-eyebrow">A connected plan, ready for your client</p>
          <h1>AI strategic plans</h1>
          <p>
            {practiceId
              ? 'Follow your assigned clients’ plans, research and reports in one place.'
              : 'Build the full plan from your company brief, with progress you can return to.'}
          </p>
        </div>
        {companyId &&
          canWrite &&
          listing?.enabled &&
          !preparation &&
          !listing.runs.some(isPlanActive) && (
            <button
              type="button"
              className="context-button"
              disabled={starting}
              onClick={() => void prepare()}
            >
              <Sparkles size={17} />
              {starting ? 'Preparing…' : 'Create a full plan'}
            </button>
          )}
      </header>
      {error && (
        <div className="context-error" role="alert">
          {error}{' '}
          <button
            type="button"
            onClick={() => {
              setError('');
              setLoading(true);
              void refresh()
                .catch((failure) => setError(failure.message))
                .finally(() => setLoading(false));
            }}
          >
            Try again
          </button>
        </div>
      )}
      {pollError && (
        <p className="ai-plan-notice" role="status">
          {pollError} We’ll keep trying. Your work is saved.
        </p>
      )}
      {loading && (
        <div className="ai-plan-empty" role="status">
          <Loader2 className="context-spin" /> Loading AI plans…
        </div>
      )}
      {!loading && listing && !listing.enabled && (
        <section className="ai-plan-empty">
          <Sparkles size={28} />
          <h2>Full-plan generation is not available yet</h2>
          <p>Your saved plans, reports and company context remain available below.</p>
          {companyId && (
            <Link to={`/company/${companyId}/context`}>
              Open company context <ArrowRight size={15} />
            </Link>
          )}
        </section>
      )}
      {!loading && listing && (listing.enabled || listing.runs.length > 0 || selected) && (
        <>
          {preparation && companyId ? (
            <PlanWizard
              key={companyId}
              companyId={companyId}
              preparation={preparation}
              onClose={() => setPreparation(null)}
              onStarted={(run) => {
                setPreparation(null);
                setListing((value) =>
                  value
                    ? { ...value, runs: [run, ...value.runs.filter((item) => item.id !== run.id)] }
                    : value
                );
                selectRun(run);
                window.dispatchEvent(new Event(AI_PLANS_CHANGED));
              }}
            />
          ) : (
            <>
              {selected && (
                <PlanRunDetail
                  key={selected.id}
                  run={selected}
                  canWrite={canWrite}
                  generationEnabled={listing.enabled}
                  requestedQuestionId={requestedQuestionId}
                  onUpdated={(run) => {
                    if (run.companyId !== companyId) return;
                    setSelected(run);
                    if (run.id !== selected.id) selectRun(run);
                    void refresh().catch(() => undefined);
                    window.dispatchEvent(new Event(AI_PLANS_CHANGED));
                  }}
                />
              )}
              <section className="ai-plan-history" aria-label="AI plan history">
                <div className="ai-plan-section-heading">
                  <h2>{selected ? 'Run history' : 'Your plans'}</h2>
                  <button
                    type="button"
                    className="ai-plan-quiet"
                    onClick={() => void refresh().catch((failure) => setError(failure.message))}
                  >
                    <RefreshCw size={15} /> Refresh
                  </button>
                </div>
                {!listing.runs.length ? (
                  <div className="ai-plan-empty">
                    <Sparkles size={28} />
                    <h3>
                      {practiceId
                        ? 'Your clients’ AI plans will appear here'
                        : 'One brief. A complete strategic plan.'}
                    </h3>
                    <p>
                      {practiceId
                        ? 'Open a client company to prepare its first plan.'
                        : 'Review a short set of questions, choose your horizon, and we’ll build the structure, strategy, projects and client report.'}
                    </p>
                    {companyId && canWrite && listing.enabled && (
                      <button
                        type="button"
                        className="context-button"
                        disabled={starting}
                        onClick={() => void prepare()}
                      >
                        Prepare company brief <ArrowRight size={16} />
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="ai-plan-run-list">
                    {listing.runs.map((run) => (
                      <article key={run.id} className={run.id === selectedId ? 'is-selected' : ''}>
                        <div>
                          <span className={`ai-plan-status status-${run.status}`}>
                            {statusLabel(run.status, listing.enabled)}
                          </span>
                          <h3>{run.companyName || 'Strategic plan'}</h3>
                          {run.jobKind === 'exports' && (
                            <small>Exports refreshed from saved report</small>
                          )}
                          <p>
                            {dateLabel(run.createdAt)}
                            {run.settings
                              ? ` · ${dateLabel(run.settings.startDate)} – ${dateLabel(run.settings.endDate)}`
                              : ''}
                          </p>
                          {run.currentStage && (
                            <small>
                              {stages.find(([key]) => key === run.currentStage)?.[1] ||
                                run.currentStage}
                            </small>
                          )}
                          {!!run.openQuestions && (
                            <p className="ai-plan-question-count">
                              {run.openQuestions} clarification{run.openQuestions === 1 ? '' : 's'}{' '}
                              · continuing with assumptions
                            </p>
                          )}
                        </div>
                        <Link
                          className="context-button context-button-secondary"
                          to={planRunUrl(run.companyId, run.id)}
                        >
                          View plan <ArrowRight size={15} />
                        </Link>
                      </article>
                    ))}
                  </div>
                )}
              </section>
            </>
          )}
        </>
      )}
    </div>
  );
}

interface WizardDraft {
  revision: number;
  profile: CompanyBusinessProfile;
  settings: PlanSettings;
  answers: Record<string, QuestionAnswer>;
  step: number;
  questionIndex: number;
  recipient: string;
  idempotencyKey: string;
  followUps?: boolean;
  questionKeys?: string[] | null;
}
export function PlanWizard({
  companyId,
  preparation,
  onStarted,
  onClose,
}: {
  companyId: string;
  preparation: PlanPreparation;
  onStarted: (run: PlanRun) => void;
  onClose: () => void;
}) {
  const storage = draftKey(companyId, 'preparation');
  const [initial] = useState(() => {
    const cached = readDraft<WizardDraft>(storage);
    return cached?.revision === preparation.context.revision &&
      cached.profile?.name !== undefined &&
      cached.settings?.startDate &&
      cached.answers
      ? cached
      : null;
  });
  const [profile, setProfile] = useState<CompanyBusinessProfile>(
    initial?.profile || { ...emptyCompanyProfile, ...preparation.context.profile }
  );
  const [settings, setSettings] = useState<PlanSettings>(
    initial?.settings || {
      startDate: today(),
      endDate: afterYears(today(), 3),
      executionDays: 90,
      emailAlerts: true,
    }
  );
  const [answers, setAnswers] = useState<Record<string, QuestionAnswer>>(initial?.answers || {});
  const [step, setStep] = useState(initial?.step || 0);
  const [questionIndex, setQuestionIndex] = useState(initial?.questionIndex || 0);
  const [followUps, setFollowUps] = useState(initial?.followUps || false);
  const [questionKeys, setQuestionKeys] = useState<string[] | null>(() =>
    Array.isArray(initial?.questionKeys)
      ? initial.questionKeys
      : initial && initial.step > 0
        ? preparation.questions
            .filter((question) => question.scope === 'business_context')
            .map((question) => question.key)
        : null
  );
  const [recipient, setRecipient] = useState(() => {
    const candidate = initial?.recipient;
    return candidate &&
      candidate !== preparation.defaultRecipientUserId &&
      preparation.recipientOptions.some((person) => person.id === candidate)
      ? candidate
      : '';
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [revision, setRevision] = useState(preparation.context.revision);
  const savedContext = useRef(preparation.context);
  const briefReviewState =
    revision > 0 &&
    (Object.keys(emptyCompanyProfile) as ProfileField[]).every(
      (field) =>
        JSON.stringify(profile[field]) === JSON.stringify(savedContext.current.profile[field])
    )
      ? 'saved'
      : 'draft';
  const idempotencyKey = useRef(initial?.idempotencyKey || crypto.randomUUID());
  const mounted = useRef(true);
  const availableQuestions = preparation.questions.filter(
    (question) => question.scope === 'business_context'
  );
  const allQuestions = questionKeys
    ? questionKeys.flatMap((key) => availableQuestions.filter((question) => question.key === key))
    : availableQuestions;
  const alternativeRecipients = preparation.recipientOptions.filter(
    (person) => person.id !== preparation.defaultRecipientUserId
  );
  const questions = allQuestions.slice(0, followUps ? 12 : 5);
  const question = questions[questionIndex];
  const value = question ? answers[question.key] || { answer: '' } : { answer: '' };
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    saveDraft(storage, {
      revision,
      profile,
      settings,
      answers,
      step,
      questionIndex,
      recipient,
      followUps,
      questionKeys,
      idempotencyKey: idempotencyKey.current,
    });
  }, [
    storage,
    revision,
    profile,
    settings,
    answers,
    step,
    questionIndex,
    recipient,
    followUps,
    questionKeys,
  ]);
  const beginQuestions = () => {
    const remaining = availableQuestions.filter((question) => {
      const field = clarificationBriefField(question);
      if (!field) return false;
      const saved = profile[field];
      return Array.isArray(saved) ? !saved.some((item) => item.trim()) : !saved.trim();
    });
    setQuestionKeys(remaining.map((question) => question.key));
    setQuestionIndex(0);
    setStep(remaining.length ? 1 : 2);
  };
  const nextQuestion = (skip = false) => {
    if (question && !skip)
      setProfile((currentProfile) =>
        applyProfileAnswer(currentProfile, question, answerText(question, value))
      );
    if (questionIndex + 1 >= questions.length) setStep(2);
    else setQuestionIndex((index) => index + 1);
  };
  const saveAndStart = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const clean = {
        ...profile,
        name: profile.name.trim(),
        description: profile.description.trim(),
      };
      if (clean.name.length < 2 || !clean.description)
        throw new Error('Add the company name and what it does before starting.');
      if (!settings.startDate || !settings.endDate || settings.endDate <= settings.startDate)
        throw new Error('Choose an end date after the plan start date.');
      if (
        !Number.isInteger(settings.executionDays) ||
        settings.executionDays < 7 ||
        settings.executionDays > 730
      )
        throw new Error('Choose a delivery roadmap between 7 and 730 days.');
      let saved = savedContext.current;
      if (!saved.revision || JSON.stringify(clean) !== JSON.stringify(saved.profile)) {
        saved = await companyContextApi.save(companyId, saved.revision, clean);
        savedContext.current = saved;
        if (!mounted.current) return;
        setRevision(saved.revision);
        setProfile(saved.profile);
      }
      const run = await planApi.start({
        companyId,
        expectedContextRevision: saved.revision,
        idempotencyKey: idempotencyKey.current,
        settings,
        ...(recipient ? { recipientUserId: recipient } : {}),
      });
      if (!mounted.current) return;
      clearDraft(storage);
      onStarted(run);
    } catch (failure) {
      if (mounted.current)
        setError(
          failure instanceof Error
            ? failure.message
            : 'Unable to start the plan. Your answers are retained.'
        );
    } finally {
      if (mounted.current) setBusy(false);
    }
  };
  return (
    <section className="ai-plan-wizard" aria-label="Prepare a full strategic plan">
      <div className="ai-plan-section-heading">
        <div>
          <p className="context-eyebrow">Prepare once, build everything</p>
          <h2>
            {['Your company brief', 'Fill the useful gaps', 'Choose the horizon & start'][step]}
          </h2>
        </div>
        <button type="button" className="ai-plan-quiet" disabled={busy} onClick={onClose}>
          <X size={17} />
          Close
        </button>
      </div>
      <ol className="ai-plan-stepper" aria-label="Plan preparation">
        <li aria-current={step === 0 ? 'step' : undefined}>1 · Company brief</li>
        <li aria-current={step === 1 ? 'step' : undefined}>2 · Useful questions</li>
        <li aria-current={step === 2 ? 'step' : undefined}>3 · Horizon & review</li>
      </ol>
      {error && (
        <p className="context-error" role="alert">
          {error}
        </p>
      )}
      <div className="ai-plan-wizard-grid">
        <div className="ai-plan-wizard-main">
          {step === 0 && (
            <>
              <p>
                We use the company brief already saved here. Review the essentials; website research
                and documents can enrich it from the company context page.
              </p>
              {(!revision || !profile.description) && (
                <p className="ai-plan-notice">This brief will be saved before generation starts.</p>
              )}
              <label className="ai-plan-setting">
                Company name
                <input
                  type="text"
                  value={profile.name}
                  onChange={(event) =>
                    setProfile((currentProfile) => ({
                      ...currentProfile,
                      name: event.target.value,
                    }))
                  }
                  disabled={busy}
                  maxLength={200}
                />
              </label>
              <label className="ai-plan-setting">
                What does the company do?
                <textarea
                  value={profile.description}
                  onChange={(event) =>
                    setProfile((currentProfile) => ({
                      ...currentProfile,
                      description: event.target.value,
                    }))
                  }
                  disabled={busy}
                  rows={4}
                  maxLength={4000}
                  placeholder="Products, services and the clients you serve."
                />
              </label>
              <details className="ai-plan-review-notes">
                <summary>Review all saved company details (optional)</summary>
                <ProfileEditor
                  profile={profile}
                  onChange={setProfile}
                  facts={preparation.context.facts}
                  sources={preparation.context.sources}
                  disabled={busy}
                />
              </details>
              <Link to={`/company/${companyId}/context`}>
                Research website or add documents <ArrowRight size={15} />
              </Link>
              <div className="ai-plan-actions">
                <button
                  type="button"
                  className="context-button"
                  disabled={profile.name.trim().length < 2 || !profile.description.trim()}
                  onClick={beginQuestions}
                >
                  Continue <ArrowRight size={16} />
                </button>
              </div>
            </>
          )}
          {step === 1 && question && (
            <>
              <p className="context-eyebrow">
                Question {questionIndex + 1} of {questions.length} · a few specific details help
              </p>
              <QuestionCard
                key={question.key}
                question={question}
                value={value}
                disabled={busy}
                onChange={(answer) =>
                  setAnswers((currentAnswers) => ({ ...currentAnswers, [question.key]: answer }))
                }
              />
              {question.assumption && (
                <p className="ai-plan-assumption">If unknown: {question.assumption}</p>
              )}
              <div className="ai-plan-actions">
                <button
                  type="button"
                  className="context-button"
                  disabled={!value.choiceId && !value.answer.trim()}
                  onClick={() => nextQuestion()}
                >
                  Save answer & continue <ArrowRight size={16} />
                </button>
                <button type="button" className="ai-plan-quiet" onClick={() => nextQuestion(true)}>
                  Skip / I don’t know
                </button>
              </div>
              <button
                type="button"
                className="ai-plan-quiet"
                onClick={() => {
                  if (value.choiceId || value.answer.trim())
                    setProfile((currentProfile) =>
                      applyProfileAnswer(currentProfile, question, answerText(question, value))
                    );
                  setStep(2);
                }}
              >
                Finish questions and review
              </button>
            </>
          )}
          {step === 2 && (
            <>
              <div className="ai-plan-setting-grid">
                <label>
                  Plan starts
                  <input
                    type="date"
                    value={settings.startDate}
                    onChange={(event) =>
                      setSettings((currentSettings) => ({
                        ...currentSettings,
                        startDate: event.target.value,
                      }))
                    }
                    disabled={busy}
                  />
                </label>
                <label>
                  Plan ends
                  <input
                    type="date"
                    value={settings.endDate}
                    min={settings.startDate}
                    onChange={(event) =>
                      setSettings((currentSettings) => ({
                        ...currentSettings,
                        endDate: event.target.value,
                      }))
                    }
                    disabled={busy}
                  />
                </label>
              </div>
              <div className="ai-plan-presets" aria-label="Planning horizon">
                {[1, 3, 5].map((years) => (
                  <button
                    key={years}
                    type="button"
                    className="ai-plan-quiet"
                    disabled={busy || !settings.startDate}
                    onClick={() =>
                      setSettings((currentSettings) => ({
                        ...currentSettings,
                        endDate: afterYears(currentSettings.startDate, years),
                      }))
                    }
                  >
                    {years} year{years > 1 ? 's' : ''}
                  </button>
                ))}
              </div>
              <label className="ai-plan-setting">
                Initial delivery roadmap (days)
                <input
                  type="number"
                  min={7}
                  max={730}
                  value={settings.executionDays}
                  onChange={(event) =>
                    setSettings((currentSettings) => ({
                      ...currentSettings,
                      executionDays: Number(event.target.value),
                    }))
                  }
                  disabled={busy}
                />
              </label>
              <label className="ai-plan-check">
                <input
                  type="checkbox"
                  checked={settings.emailAlerts}
                  onChange={(event) =>
                    setSettings((currentSettings) => ({
                      ...currentSettings,
                      emailAlerts: event.target.checked,
                    }))
                  }
                  disabled={busy}
                />
                <span>Email clarification alerts</span>
              </label>
              {settings.emailAlerts && alternativeRecipients.length > 0 && (
                <label className="ai-plan-setting">
                  Email updates to
                  <select
                    value={recipient}
                    onChange={(event) => setRecipient(event.target.value)}
                    disabled={busy}
                  >
                    <option value="">Me · the adviser starting this plan</option>
                    {alternativeRecipients.map((person) => (
                      <option key={person.id} value={person.id}>
                        {person.name || person.email} · {person.email}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {settings.emailAlerts && (
                <p className="context-help">
                  Alerts go to the adviser starting this plan by default, or to the adviser you
                  choose.
                </p>
              )}
              <div className="ai-plan-deliverables">
                <h3>Your finished plan includes</h3>
                <ul>
                  {stages.map(([key, label]) => (
                    <li key={key}>
                      <CheckCircle2 size={15} />
                      {label}
                    </li>
                  ))}
                </ul>
              </div>
              <p className="ai-plan-notice">
                Existing work is preserved. AI fills gaps, researches competitors and completes
                assessments. Uncertain points stay labelled as assumptions; you can answer them
                while work continues.
              </p>
              {allQuestions.length > 5 && !followUps && (
                <button
                  type="button"
                  className="ai-plan-quiet"
                  disabled={busy}
                  onClick={() => {
                    setFollowUps(true);
                    setQuestionIndex(5);
                    setStep(1);
                  }}
                >
                  Answer more useful questions (optional)
                </button>
              )}
              <button
                type="button"
                className="ai-plan-quiet"
                onClick={() => setEditing(!editing)}
                disabled={busy}
              >
                {editing ? 'Hide brief editor' : 'Add more detail to the brief'}
              </button>
              {editing && <ProfileEditor profile={profile} onChange={setProfile} disabled={busy} />}
              <div className="ai-plan-actions">
                <button
                  type="button"
                  className="context-button"
                  disabled={
                    busy ||
                    !preparation.enabled ||
                    settings.executionDays < 7 ||
                    settings.executionDays > 730
                  }
                  onClick={() => void saveAndStart()}
                >
                  {busy ? <Loader2 size={17} className="context-spin" /> : <Sparkles size={17} />}
                  {busy ? 'Saving brief & starting…' : 'Save company context & start plan'}
                </button>
              </div>
              <p className="context-help">
                You can leave this page. Your plan and downloads are generated in the background.
              </p>
            </>
          )}
          {step > 0 && (
            <button
              type="button"
              className="ai-plan-quiet"
              disabled={busy}
              onClick={() => {
                if (step === 1 && questionIndex > 0) setQuestionIndex((index) => index - 1);
                else setStep(step === 2 && questions.length ? 1 : 0);
              }}
            >
              <ArrowLeft size={15} />
              Back
            </button>
          )}
        </div>
        <BusinessBrief
          profile={profile}
          facts={preparation.context.facts}
          sources={preparation.context.sources}
          editable={false}
          reviewState={briefReviewState}
          collapsible
        />
      </div>
    </section>
  );
}

export function PlanRunDetail({
  run,
  onUpdated,
  canWrite = true,
  generationEnabled = true,
  requestedQuestionId,
}: {
  run: PlanRun;
  onUpdated: (run: PlanRun) => void;
  canWrite?: boolean;
  generationEnabled?: boolean;
  requestedQuestionId?: string;
}) {
  const canMutate = canWrite && run.canEditRun !== false;
  const exportOnly = run.jobKind === 'exports';
  const displayedStages = exportOnly ? stages.filter(([key]) => key === 'exports') : stages;
  const cents = (value: number | undefined) =>
    typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : 0;
  const money = (value: number) => `US$${(value / 100).toFixed(2)}`;
  const committedCents = cents(run.costCents) + cents(run.reservedCents);
  const exportRefreshStorage = draftKey(run.companyId, `${run.id}:refresh-exports`);
  const exportRefreshKey = useRef(
    readDraft<{ idempotencyKey: string }>(exportRefreshStorage)?.idempotencyKey ||
      crypto.randomUUID()
  );
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    if (
      !requestedQuestionId ||
      !run.clarifications?.some((question) => question.id === requestedQuestionId)
    )
      return;
    const card = document.getElementById(`ai-plan-question-${requestedQuestionId}`);
    card?.scrollIntoView?.({ block: 'center' });
    card?.focus({ preventScroll: true });
  }, [run.id, requestedQuestionId]);
  const action = async (key: string, work: () => Promise<PlanRun | void>) => {
    if (busy) return;
    setBusy(key);
    setError('');
    try {
      const next = await work();
      if (next && alive.current) onUpdated(next);
    } catch (failure) {
      if (alive.current)
        setError(failure instanceof Error ? failure.message : 'Unable to update this plan.');
    } finally {
      if (alive.current) setBusy(null);
    }
  };
  const refreshExports = async () => {
    saveDraft(exportRefreshStorage, { idempotencyKey: exportRefreshKey.current });
    const next = await planApi.refreshExports(run.id, exportRefreshKey.current);
    clearDraft(exportRefreshStorage);
    exportRefreshKey.current = crypto.randomUUID();
    return next;
  };
  const complete = (run.steps || []).filter(
    (step) =>
      displayedStages.some(([key]) => key === step.key) &&
      (step.status === 'completed' || step.status === 'skipped')
  ).length;
  const open = (run.clarifications || []).filter((question) => question.status === 'open');
  const answered = (run.clarifications || []).filter((question) =>
    ['answered', 'resolved'].includes(question.status)
  );
  const historical = (run.clarifications || []).filter(
    (question) => !['open', 'answered', 'resolved'].includes(question.status)
  );
  const latestUpdate = run.revisions?.find(
    (revision) => revision.id === run.latestRevisionId && revision.id !== run.id
  );
  const activeUpdate = run.revisions?.find(
    (revision) => revision.id !== run.id && ['queued', 'running'].includes(revision.status)
  );
  const updatingReport = run.updatingReport || !!activeUpdate;
  const updateNeedsAttention =
    !updatingReport && latestUpdate && ['failed', 'needs_attention'].includes(latestUpdate.status);
  const latest =
    latestUpdate && ['completed', 'completed_with_warnings'].includes(latestUpdate.status)
      ? latestUpdate
      : undefined;
  const displayedStatus = updatingReport
    ? generationEnabled
      ? 'Updating report'
      : 'Report update paused'
    : updateNeedsAttention
      ? 'Update needs attention'
      : statusLabel(run.status, generationEnabled);
  const displayedStatusClass = updatingReport
    ? 'running'
    : updateNeedsAttention
      ? 'needs_attention'
      : run.status;
  return (
    <section className="ai-plan-detail" aria-label="AI plan progress">
      <div className="ai-plan-section-heading">
        <div>
          <span className={`ai-plan-status status-${displayedStatusClass}`}>{displayedStatus}</span>
          <h2>{run.companyName || 'Your strategic plan'}</h2>
          {exportOnly && <p>Exports refreshed from saved report. Earlier plan work is reused.</p>}
          <p>
            Started {dateLabel(run.createdAt)} · company brief revision {run.contextRevision}
          </p>
          {run.currentStage && isPlanActive(run) && (
            <p>
              Current stage:{' '}
              {stages.find(([key]) => key === run.currentStage)?.[1] || run.currentStage}
            </p>
          )}
        </div>
        {canMutate && isPlanActive(run) && (
          <button
            type="button"
            className="ai-plan-quiet"
            disabled={!!busy}
            onClick={() => void action('cancel', () => planApi.cancel(run.id))}
          >
            {busy === 'cancel' ? 'Stopping…' : 'Stop this run'}
          </button>
        )}
        {canMutate && generationEnabled && ['failed', 'needs_attention'].includes(run.status) && (
          <button
            type="button"
            className="context-button context-button-secondary"
            disabled={!!busy}
            onClick={() => void action('retry', () => planApi.retry(run.id))}
          >
            <RefreshCw size={16} />
            {busy === 'retry' ? 'Retrying…' : 'Retry unfinished stages'}
          </button>
        )}
      </div>
      {error && (
        <p className="context-error" role="alert">
          {error}
        </p>
      )}
      {run.error && (
        <p className="context-error" role="alert">
          {run.error}
        </p>
      )}
      {updatingReport && (
        <p className="ai-plan-notice" role="status">
          {generationEnabled
            ? 'Updating the plan and preparing fresh report downloads. The previous report remains available until the new version is ready.'
            : 'Report updates are temporarily paused. The previous report remains available.'}{' '}
          {activeUpdate && (
            <Link to={planRunUrl(run.companyId, activeUpdate.id)}>
              View update progress <ArrowRight size={15} />
            </Link>
          )}
        </p>
      )}
      {updateNeedsAttention && latestUpdate && (
        <p className="ai-plan-notice ai-plan-update-warning" role="alert">
          The latest report update needs attention. Your previous report and downloads remain
          available.{' '}
          <Link to={planRunUrl(run.companyId, latestUpdate.id)}>
            Review report update <ArrowRight size={15} />
          </Link>
        </p>
      )}
      {isPlanActive(run) && (
        <p className="ai-plan-notice" role="status">
          {generationEnabled
            ? exportOnly
              ? 'Your report files continue in the background if you close this page.'
              : 'Your plan continues if you close this page. Completed work is saved as each stage finishes.'
            : 'Generation is temporarily paused. Your saved plan, answers and report files remain available.'}
        </p>
      )}
      {(run.costCents !== undefined || run.reservedCents !== undefined) && (
        <div className="ai-plan-notice">
          <strong>
            AI usage allowance committed: {money(committedCents)}
            {cents(run.budgetCents) > 0 ? ` of ${money(cents(run.budgetCents))}` : ''}
          </strong>
          <p>
            This run’s allowance includes recorded usage and conservative allowances for pending or
            unknown provider usage. Final provider billing may differ.
          </p>
        </div>
      )}
      <div className="ai-plan-progress-summary">
        <strong>
          {complete} of {displayedStages.length} stages complete
        </strong>
        <span>Updated {dateLabel(run.updatedAt)}</span>
      </div>
      <progress
        value={complete}
        max={displayedStages.length}
        aria-label="AI plan generation progress"
      />
      <ol className="ai-plan-stage-list">
        {displayedStages.map(([key, label]) => {
          const state = run.steps?.find((step) => step.key === key);
          const finished = ['completed', 'skipped'].includes(state?.status || '');
          const counts = finished ? stageResultCounts(state?.result) : null;
          const working =
            state?.status === 'running' || (run.currentStage === key && isPlanActive(run));
          return (
            <li key={key} className={working ? 'is-working' : finished ? 'is-finished' : ''}>
              <span>
                {finished ? (
                  <Check size={18} />
                ) : working ? (
                  <Loader2 size={18} className="context-spin" />
                ) : (
                  <Circle size={16} />
                )}
              </span>
              <div>
                <strong>{label}</strong>
                <small>
                  {finished
                    ? 'Saved'
                    : working
                      ? 'Working on this stage'
                      : state?.status === 'failed'
                        ? 'Needs a retry'
                        : 'Waiting for earlier stages'}
                </small>
                {counts && (counts.generated !== undefined || counts.preserved !== undefined) && (
                  <small>
                    {[
                      counts.generated !== undefined ? `${counts.generated} outputs saved` : '',
                      counts.preserved !== undefined
                        ? `${counts.preserved} existing outputs preserved`
                        : '',
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </small>
                )}
                {counts && (counts.rated !== undefined || counts.unassessed !== undefined) && (
                  <small>
                    {[
                      counts.rated !== undefined ? `${counts.rated} criteria AI-rated` : '',
                      counts.unassessed !== undefined ? `${counts.unassessed} not assessed` : '',
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </small>
                )}
                {state?.error && <p className="context-error">{state.error}</p>}
              </div>
            </li>
          );
        })}
      </ol>
      {!!open.length && (
        <div className="ai-plan-clarifications">
          <h3>Make the plan more specific</h3>
          <p>
            We’re continuing with the assumptions below. Answer when convenient; we’ll update the
            plan and create fresh downloads. Your own edits stay available for review.
          </p>
          {open.map((question) => (
            <ClarificationCard
              key={question.id}
              question={question}
              run={run}
              canWrite={canMutate}
              onUpdated={onUpdated}
            />
          ))}
        </div>
      )}
      {!!answered.length && (
        <details
          className="ai-plan-review-notes"
          open={answered.some((question) => question.id === requestedQuestionId) || undefined}
        >
          <summary>Saved clarifications ({answered.length})</summary>
          {answered.map((question) => (
            <ClarificationCard
              key={question.id}
              question={question}
              run={run}
              canWrite={canMutate}
              onUpdated={onUpdated}
            />
          ))}
        </details>
      )}
      {!!historical.length && (
        <details
          className="ai-plan-review-notes"
          open={historical.some((question) => question.id === requestedQuestionId) || undefined}
        >
          <summary>Clarification history ({historical.length})</summary>
          {historical.map((question) => (
            <article
              className="ai-plan-question"
              id={`ai-plan-question-${question.id}`}
              key={question.id}
              tabIndex={-1}
            >
              <h3>{question.prompt}</h3>
              <p>
                {question.status === 'superseded'
                  ? 'No longer needed'
                  : question.status === 'cancelled'
                    ? 'Cancelled'
                    : question.status === 'dismissed'
                      ? 'Dismissed'
                      : 'Archived'}
                {' · No answer is needed for this question.'}
              </p>
              {question.reason && <p>{question.reason}</p>}
              {!!question.choices?.length && (
                <ul>
                  {question.choices.map((choice) => (
                    <li key={choice.id}>
                      <strong>{choice.label}</strong>
                      {choice.description ? ` · ${choice.description}` : ''}
                    </li>
                  ))}
                </ul>
              )}
              {question.assumption && (
                <p className="ai-plan-assumption">
                  <strong>Previous assumption:</strong> {question.assumption}
                </p>
              )}
            </article>
          ))}
        </details>
      )}
      {latest && (
        <p className="ai-plan-notice">
          Your updated plan and fresh downloads are ready.{' '}
          <Link to={planRunUrl(run.companyId, latest.id)}>
            Open updated plan <ArrowRight size={15} />
          </Link>
        </p>
      )}
      {!!run.warnings?.length && (
        <details className="ai-plan-review-notes" open={run.status === 'needs_attention'}>
          <summary>Review notes ({run.warnings.length})</summary>
          <ul>
            {run.warnings.map((warning, index) => (
              <li key={index}>{warning}</li>
            ))}
          </ul>
        </details>
      )}
      {!!run.emailDeliveries?.length && <EmailDeliveryStatus deliveries={run.emailDeliveries} />}
      {run.reportId && (
        <div className="ai-plan-results">
          <Link className="context-button" to={planReportUrl(run)}>
            Open editable report <ArrowRight size={16} />
          </Link>
          {(run.artifacts || [])
            .filter(
              (artifact) =>
                !artifact.status || artifact.status === 'ready' || artifact.status === 'completed'
            )
            .map((artifact) => (
              <button
                key={artifact.id}
                type="button"
                className="context-button context-button-secondary"
                disabled={!!busy}
                onClick={() => void action(artifact.id, () => planApi.download(run.id, artifact))}
              >
                <Download size={16} />
                {busy === artifact.id
                  ? 'Downloading…'
                  : artifact.format.toLowerCase() === 'pptx'
                    ? 'Download PowerPoint'
                    : 'Download PDF'}
              </button>
            ))}
          {canMutate &&
            generationEnabled &&
            ['completed', 'completed_with_warnings'].includes(run.status) &&
            !updatingReport && (
              <button
                type="button"
                className="ai-plan-quiet"
                disabled={!!busy}
                onClick={() => void action('refresh-exports', refreshExports)}
              >
                <RefreshCw size={16} />
                {busy === 'refresh-exports' ? 'Refreshing report files…' : 'Refresh report files'}
              </button>
            )}
        </div>
      )}
      {canMutate &&
        generationEnabled &&
        run.reportId &&
        ['completed', 'completed_with_warnings'].includes(run.status) && (
          <p className="context-help">
            Refresh report files after saving edits to the report. New PDF and PowerPoint files are
            prepared in the background.
          </p>
        )}
      {!!run.revisions?.length && (
        <details className="ai-plan-review-notes">
          <summary>Report revisions ({run.revisions.length})</summary>
          <ul>
            {run.revisions.map((revision) => (
              <li key={revision.id}>
                <Link to={planRunUrl(run.companyId, revision.id)}>
                  {dateLabel(revision.createdAt)} ·{' '}
                  {statusLabel(revision.status, generationEnabled)}
                </Link>
              </li>
            ))}
          </ul>
        </details>
      )}
      <div className="ai-plan-open-work">
        <Link to={`/company/${run.companyId}/organization`}>People & structure</Link>
        <Link to={`/company/${run.companyId}/strategy`}>Strategic plan</Link>
        <Link to={`/company/${run.companyId}/execution`}>Projects & tasks</Link>
      </div>
    </section>
  );
}

function EmailDeliveryStatus({
  deliveries,
}: {
  deliveries: NonNullable<PlanRun['emailDeliveries']>;
}) {
  const hasProblem = deliveries.some(
    (delivery) =>
      delivery.lastError ||
      ['attention', 'failed', 'bounced', 'complained'].includes(delivery.status)
  );
  return (
    <details className="ai-plan-review-notes" open={hasProblem}>
      <summary>Email update status ({deliveries.length})</summary>
      <ul>
        {deliveries.map((delivery) => {
          const delivered = !!delivery.deliveredAt || delivery.status === 'delivered';
          const label = delivered
            ? 'Delivery confirmed'
            : delivery.status === 'accepted'
              ? 'Accepted by email provider · delivery not yet confirmed'
              : delivery.status === 'pending'
                ? delivery.attempts
                  ? 'Retry scheduled'
                  : 'Queued'
                : delivery.status === 'sending'
                  ? 'Sending'
                  : delivery.status === 'suppressed'
                    ? 'Email skipped · no longer needed'
                    : ['attention', 'failed', 'bounced', 'complained'].includes(delivery.status)
                      ? 'Email delivery needs attention'
                      : statusLabel(delivery.status);
          return (
            <li key={delivery.id}>
              <strong>{label}</strong>
              {delivery.lastError && <p>{delivery.lastError}</p>}
              {delivery.status === 'pending' && delivery.nextAttemptAt && (
                <p>
                  Next attempt{' '}
                  {new Date(delivery.nextAttemptAt).toLocaleString('en-AU', {
                    day: 'numeric',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZone: 'Australia/Sydney',
                  })}
                </p>
              )}
            </li>
          );
        })}
      </ul>
      {hasProblem && (
        <p>
          Your questions and saved reports remain available here while email delivery is resolved.
        </p>
      )}
    </details>
  );
}

function ClarificationCard({
  question,
  run,
  onUpdated,
  canWrite,
}: {
  question: PlanClarification;
  run: PlanRun;
  onUpdated: (run: PlanRun) => void;
  canWrite: boolean;
}) {
  const storage = draftKey(run.companyId, `${run.id}:${question.id}`);
  const [value, setValue] = useState<QuestionAnswer>(() => {
    const cached = readDraft<QuestionAnswer>(storage);
    return cached && typeof cached.answer === 'string' ? cached : { answer: '' };
  });
  const [submitted, setSubmitted] = useState(() =>
    typeof question.answer === 'string'
      ? question.answer
      : question.answer?.text ||
        question.answer?.answer ||
        (question.answer?.choiceId
          ? question.choices?.find(
              (choice) => choice.id === (question.answer as { choiceId?: string }).choiceId
            )?.label || ''
          : '')
  );
  const wasAnswered = ['answered', 'resolved'].includes(question.status);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    saveDraft(storage, value);
  }, [storage, value]);
  const answer = async () => {
    setBusy(true);
    setError('');
    try {
      const text = answerText(question, value);
      const next = await planApi.answer(run.id, question.id, {
        expectedInputRevision: run.inputRevision,
        ...(value.choiceId ? { choiceId: value.choiceId } : { answer: value.answer.trim() }),
        saveToContext: false,
      });
      if (!mounted.current) return;
      setSubmitted(text);
      clearDraft(storage);
      setNotice('Answer saved for this run. Updates and fresh downloads are being prepared.');
      onUpdated(next);
    } catch (failure) {
      if (mounted.current)
        setError(failure instanceof Error ? failure.message : 'Your answer could not be saved.');
    } finally {
      if (mounted.current) setBusy(false);
    }
  };
  const saveToBrief = async () => {
    setBusy(true);
    setError('');
    try {
      const context = await companyContextApi.get(run.companyId);
      await planApi.saveAnswerToContext(run.id, question.id, context.revision);
      if (mounted.current) {
        setNotice('Answer saved to the company brief for future plans.');
        try {
          const next = await planApi.get(run.id);
          if (mounted.current) onUpdated(next);
        } catch {
          if (mounted.current)
            setNotice(
              'Answer saved to the company brief. Refresh this page to see the updated run.'
            );
        }
      }
    } catch (failure) {
      if (mounted.current)
        setError(
          failure instanceof Error ? failure.message : 'Could not update the company brief.'
        );
    } finally {
      if (mounted.current) setBusy(false);
    }
  };
  return (
    <article className="ai-plan-question" id={`ai-plan-question-${question.id}`} tabIndex={-1}>
      <QuestionCard
        question={question}
        value={submitted ? { answer: submitted } : value}
        disabled={busy || !!submitted || wasAnswered || run.status === 'cancelled' || !canWrite}
        onChange={setValue}
      />
      {question.assumption && (
        <p className="ai-plan-assumption">
          <strong>Current assumption:</strong> {question.assumption}
        </p>
      )}
      {error && (
        <p className="context-error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="context-success" role="status">
          {notice}
        </p>
      )}
      <div className="ai-plan-actions">
        {canWrite && !submitted && !wasAnswered && (
          <button
            type="button"
            className="context-button context-button-secondary"
            disabled={
              busy || run.status === 'cancelled' || (!value.choiceId && !value.answer.trim())
            }
            onClick={() => void answer()}
          >
            {busy ? 'Saving…' : 'Save answer & update this plan'}
          </button>
        )}
        {canWrite && (submitted || wasAnswered) && clarificationBriefField(question) && (
          <button
            type="button"
            className="ai-plan-quiet"
            disabled={busy}
            onClick={() => void saveToBrief()}
          >
            Also save this answer to company brief
          </button>
        )}
      </div>
    </article>
  );
}
